import type { PaigeClient } from '@paige/api-client';
import { tenantHeader, unwrap } from './http';
import type {
  AccountNotice,
  CreditAlerts,
  CreditAlertsInput,
  CreditAlertTest,
  CreditBanner,
} from './types';

/**
 * The company's credit alerts (docs/pricing.md, "Credit alerts"): read by admins and managers,
 * saved and tested by an admin; the banner is read by everyone in the company.
 */
export interface CreditAlertsApi {
  get(tenant: string): Promise<CreditAlerts>;
  save(tenant: string, input: CreditAlertsInput): Promise<CreditAlerts>;
  /** Sends a test to the saved recipients; answers who it went to. */
  test(tenant: string): Promise<CreditAlertTest>;
  banner(tenant: string): Promise<CreditBanner>;
  /** The account banner beside it: the one problem with the plan, everyone in the company reads it. */
  accountNotice(tenant: string): Promise<AccountNotice>;
}

export function creditAlertsApi(client: PaigeClient): CreditAlertsApi {
  const h = (tenant: string) => ({ params: { header: tenantHeader(tenant) } });
  return {
    async get(tenant) {
      return unwrap(await client.GET('/company/credit-alerts', h(tenant)));
    },
    async save(tenant, input) {
      return unwrap(await client.PUT('/company/credit-alerts', { ...h(tenant), body: input }));
    },
    async test(tenant) {
      return unwrap(await client.POST('/company/credit-alerts/test', h(tenant)));
    },
    async banner(tenant) {
      return unwrap(await client.GET('/company/credit-banner', h(tenant)));
    },
    async accountNotice(tenant) {
      return unwrap(await client.GET('/company/account-notice', h(tenant)));
    },
  };
}
